<template>
  <v-data-table
    :headers="getHeaders"
    :items="showDeleted ? items : items.filter(x => x.is_deleted === false)"
    :search="getSearchModel"
    :loading="loading"
    :page.sync="getPageModel"
    item-key="id"
    sort-by="nom"
    :sort-desc="false"
    :mobile-breakpoint="0"
    :hide-default-header="$vuetify.breakpoint.xsOnly"
    hide-default-footer
    :items-per-page="$vuetify.breakpoint.xsOnly ? 6 : 10"
    @page-count="pageCount = $event"
    :custom-filter="customFilter"
  >
    <template v-slot:item.mobile="{ item }">
      <div class="my-1">
        <v-row align="center" justify="start" no-gutters>
          <span class="font-capitalize font-weight-bold"> {{ item.nom }}</span>
        </v-row>
        <v-row align="center" justify="start" no-gutters v-if="item.data">
          <span class="caption">{{
            (item.data.prix_inventory / item.data.quantity_inventory) | round4
          }}</span>
          <span class="caption ml-1"
            >$/{{ item.data.unite_mesure_inventory }}</span
          >
        </v-row>
      </div>
    </template>
    <template v-slot:item.actions="{ item }">
      <v-row justify="end" no-gutters>
        <v-btn
          v-if="getIsAdmin"
          icon
          :small="!$vuetify.breakpoint.xsOnly"
          :to="{ name: 'StatisticsDetails', params: { id: item.id } }"
        >
          <v-icon :small="!$vuetify.breakpoint.xsOnly">far fa-chart-bar</v-icon>
        </v-btn>
        <v-btn
          v-if="getIsAdmin"
          icon
          :small="!$vuetify.breakpoint.xsOnly"
          @click="openProductHandler(item)"
        >
          <v-icon :small="!$vuetify.breakpoint.xsOnly">far fa-edit</v-icon>
        </v-btn>
      </v-row>
    </template>
    <template v-slot:item.est_intermediaire="{ item }">
      <v-row align="center" justify="start" no-gutters>
        <v-tooltip
          bottom
          v-if="!item.data || item.data.need_configuration === true"
        >
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" color="warning" small>
              fa-exclamation-triangle
            </v-icon>
          </template>
          <span>Produit à configurer pour cette succursale</span>
        </v-tooltip>
        <v-tooltip bottom v-if="item.data && item.data.tags">
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              text
              small
              v-on="on"
              :disabled="item.data.tags.length === 0"
            >
              <v-icon small>fa-hashtag</v-icon>
            </v-btn>
          </template>
          <span>{{ item.data.tags.join(", ") }}</span>
        </v-tooltip>
        <v-tooltip bottom v-if="item.est_intermediaire === true">
          <template v-slot:activator="{ on }">
            <v-btn icon text small v-on="on">
              <v-icon small>fa-mitten</v-icon>
            </v-btn>
          </template>
          <span>Est une recette intermédiaire</span>
        </v-tooltip>
        <v-icon
          left
          small
          v-if="item.is_deleted === true"
          color="error"
          class="mx-1"
          >fa-trash</v-icon
        >
      </v-row>
    </template>
    <template v-slot:item.nom="{ item }">
      <div
        style="width: 100%; cursor: pointer;"
        @click="openProductHandler(item)"
      >
        <span class="font-capitalize font-weight-bold">{{ item.nom }}</span>
      </div>
    </template>
    <template v-slot:item.updated="{ item }">
      <span>
        {{ item.updated | localFormat }}
      </span>
    </template>
    <template v-slot:item.prix="{ item }">
      <div v-if="item.data">
        <div v-if="item.data.use_order_quantity && item.data.price_order !== 0">
          <span>{{
            (item.data.price_order / item.data.quantity_order) | round4
          }}</span>
          <span v-if="item.data" class="caption ml-1"
            >$/{{ item.data.unite_mesure_order }}</span
          >
        </div>
        <div v-else-if="item.data.prix_inventory !== 0">
          <span>{{
            (item.data.prix_inventory / item.data.quantity_inventory) | round4
          }}</span>
          <span v-if="item.data" class="caption ml-1"
            >$/{{ item.data.unite_mesure_inventory }}</span
          >
        </div>
      </div>
    </template>
    <template v-slot:top>
      <v-row align="center" justify="end">
        <v-col cols="12" sm="6">
          <v-text-field
            dense
            filled
            hide-details
            single-line
            clearable
            prepend-inner-icon="fa-search"
            v-model="getSearchModel"
            class="no-before"
          ></v-text-field>
        </v-col>
      </v-row>
    </template>
    <template v-slot:footer>
      <v-pagination
        color="accent"
        v-model="getPageModel"
        :length="pageCount"
        :total-visible="5"
      ></v-pagination>
    </template>
  </v-data-table>
</template>

<script>
import { mapGetters } from "vuex";
export default {
  name: "RecipeProductGrid",
  props: {
    items: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    ...mapGetters(["getIntermediateRecipe", "getIsAdmin"]),
    getHeaders() {
      if (this.$vuetify.breakpoint.xsOnly) {
        return [
          { value: "mobile", text: "" },
          { value: "actions", text: "" }
        ];
      } else {
        if (this.getIsAdmin) {
          return [
            {
              value: "est_intermediaire",
              text: "",
              width: 150,
              sortable: false
            },
            { value: "nom", text: "Nom" },
            { value: "prix", text: "Prix" },
            { value: "updated", text: "Dernière mise à jour", width: 175 },
            { value: "actions", text: "", width: 100, sortable: false }
          ];
        } else {
          return [
            { value: "est_intermediaire", text: "", width: 150 },
            { value: "nom", text: "Nom" },
            { value: "updated", text: "Dernière mise à jour", width: 175 },
            { value: "actions", text: "", width: 0 }
          ];
        }
      }
    },
    getPageModel: {
      get() {
        return this.$store.getters.getProductPage;
      },
      set(v) {
        this.$store.commit("setProductPage", v);
      }
    },
    getSearchModel: {
      get() {
        return this.$store.getters.getProductSearch;
      },
      set(v) {
        this.$store.commit("setProductSearch", v);
      }
    }
  },
  data() {
    return {
      showDeleted: false
    };
  },
  methods: {
    openProductHandler(item) {
      if (item.est_intermediaire === true) {
        const recipeToEdit = this.getIntermediateRecipe.find(
          r => r.fk_produit_intermediaire === item.id
        );
        this.$router.push({
          name: "RecipeEditor",
          params: { id: recipeToEdit.id }
        });
      } else {
        this.$router.push({
          name: "ProductEditor",
          params: { id: item.id }
        });
      }
    },
    customFilter(value, search, item) {
      if (search !== null) {
        const normalizeSearch = search
          .toLowerCase()
          .replace(/\s/g, "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
        const itemName = item.nom
          .toLowerCase()
          .replace(/\s/g, "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
        const itemCode =
          item.data && item.data.order_number !== null
            ? item.data.order_number
                .toLowerCase()
                .replace(/\s/g, "")
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
            : null;
        const itemKey =
          item.data && item.data.product_key !== null
            ? item.data.product_key
                .toLowerCase()
                .replace(/\s/g, "")
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
            : null;

        if (itemName.indexOf(normalizeSearch) !== -1) {
          return true;
        } else if (
          itemCode !== null &&
          itemCode.indexOf(normalizeSearch) !== -1
        ) {
          return true;
        } else if (
          itemKey !== null &&
          itemKey.indexOf(normalizeSearch) !== -1
        ) {
          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
